import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

const ROLES = ['teacher', 'pupil', 'student'] as const;
type Role = (typeof ROLES)[number];
type KeyMetadata = {
    site_key: string;
    secret_configured: boolean;
    secret_key_masked: string;
};
type KeyState = {
    versionId: string;
    secretName: string;
    applications: Record<Role, KeyMetadata>;
};
type KeyInput = { site_key: string; secret_key: string };

async function requestKeys(
    signal?: AbortSignal,
    update?: {
        versionId: string;
        applications: Partial<
            Record<Role, { site_key: string; secret_key?: string }>
        >;
    }
): Promise<KeyState> {
    const response = await fetch(
        `${process.env.REACT_APP_INFRA_API}/admin/turnstile-keys`,
        {
            method: update ? 'PUT' : 'GET',
            cache: 'no-store',
            signal,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            ...(update ? { body: JSON.stringify(update) } : {}),
        }
    );
    if (!response.ok) {
        throw new Error(
            response.status === 409
                ? 'hallinta.settings.turnstile_keys_conflict'
                : 'hallinta.settings.turnstile_keys_error'
        );
    }
    return response.json();
}

const TurnstileKeysDialog = ({
    environment,
    onClose,
}: {
    environment: string;
    onClose: () => void;
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [state, setState] = useState<KeyState>();
    const [inputs, setInputs] = useState<Record<Role, KeyInput>>();
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        requestKeys(controller.signal)
            .then(data => {
                setState(data);
                setInputs({
                    teacher: {
                        site_key: data.applications.teacher.site_key,
                        secret_key: '',
                    },
                    pupil: {
                        site_key: data.applications.pupil.site_key,
                        secret_key: '',
                    },
                    student: {
                        site_key: data.applications.student.site_key,
                        secret_key: '',
                    },
                });
            })
            .catch((err: Error) => {
                if (!controller.signal.aborted) setError(err.message);
            });
        return () => controller.abort();
    }, []);

    const updates: Partial<
        Record<Role, { site_key: string; secret_key?: string }>
    > = {};
    let invalid = false;
    if (state && inputs) {
        for (const role of ROLES) {
            const input = inputs[role];
            const siteKey = input.site_key.trim();
            const secretKey = input.secret_key.trim();
            const previous = state.applications[role];
            if (siteKey !== previous.site_key || secretKey) {
                updates[role] = {
                    site_key: siteKey,
                    ...(secretKey ? { secret_key: secretKey } : {}),
                };
                if (
                    !siteKey ||
                    ((!previous.secret_configured ||
                        siteKey !== previous.site_key) &&
                        !secretKey)
                ) {
                    invalid = true;
                }
            }
        }
    }

    const save = async () => {
        if (!state || invalid || !Object.keys(updates).length) return;
        setSaving(true);
        setError('');
        try {
            await requestKeys(undefined, {
                versionId: state.versionId,
                applications: updates,
            });
            notify('hallinta.settings.turnstile_keys_saved', {
                type: 'success',
            });
            onClose();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'hallinta.settings.turnstile_keys_error'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                {translate('hallinta.settings.turnstile_keys_title')} -{' '}
                {environment}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="warning">
                        {translate('hallinta.settings.turnstile_keys_restart')}
                    </Alert>
                    <Typography variant="body2">
                        {translate('hallinta.settings.turnstile_keys_help')}
                    </Typography>
                    {error && (
                        <Alert severity="error">{translate(error)}</Alert>
                    )}
                    {!state && !error && <CircularProgress />}
                    {state && inputs && (
                        <>
                            <Typography variant="caption">
                                {state.secretName}
                            </Typography>
                            {ROLES.map(role => (
                                <Stack spacing={1} key={role}>
                                    <Typography variant="subtitle2">
                                        {translate(
                                            `hallinta.settings.turnstile_role_${role}`
                                        )}
                                    </Typography>
                                    {state.applications[role]
                                        .secret_configured && (
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: 'monospace' }}
                                        >
                                            {translate(
                                                'hallinta.settings.turnstile_keys_current_secret'
                                            )}
                                            :{' '}
                                            {
                                                state.applications[role]
                                                    .secret_key_masked
                                            }
                                        </Typography>
                                    )}
                                    <TextField
                                        label="Site key"
                                        value={inputs[role].site_key}
                                        disabled={saving}
                                        slotProps={{
                                            htmlInput: { maxLength: 256 },
                                        }}
                                        onChange={event =>
                                            setInputs({
                                                ...inputs,
                                                [role]: {
                                                    ...inputs[role],
                                                    site_key:
                                                        event.target.value,
                                                },
                                            })
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                    <TextField
                                        label="Secret key"
                                        type="password"
                                        autoComplete="new-password"
                                        value={inputs[role].secret_key}
                                        disabled={saving}
                                        slotProps={{
                                            htmlInput: { maxLength: 256 },
                                        }}
                                        helperText={translate(
                                            state.applications[role]
                                                .secret_configured
                                                ? 'hallinta.settings.turnstile_keys_keep_secret'
                                                : 'hallinta.settings.turnstile_keys_missing_secret'
                                        )}
                                        onChange={event =>
                                            setInputs({
                                                ...inputs,
                                                [role]: {
                                                    ...inputs[role],
                                                    secret_key:
                                                        event.target.value,
                                                },
                                            })
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                </Stack>
                            ))}
                        </>
                    )}
                    {invalid && (
                        <Alert severity="info">
                            {translate(
                                'hallinta.settings.turnstile_keys_pair_required'
                            )}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button
                    onClick={save}
                    disabled={
                        !state ||
                        saving ||
                        invalid ||
                        !Object.keys(updates).length
                    }
                >
                    {translate('ra.action.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TurnstileKeysDialog;
